import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Button, Input, Select, Card, Loading, Modal } from '../components/common';
import customerService from '../services/customerService';
import transactionService from '../services/transactionService';
import customerAccountService from '../services/customerAccountService';
import { useAuth } from '../contexts/AuthContext';
import CustomerWallet from '../components/customers/CustomerWallet';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  CalendarIcon,
  DocumentTextIcon,
  KeyIcon,
  StarIcon,
  NoSymbolIcon,
  CheckCircleIcon,
  BanknotesIcon,
  ArrowPathIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';

const CustomerDetailPage = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [walletTransactions, setWalletTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [settingPassword, setSettingPassword] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const { register: registerPassword, handleSubmit: handlePasswordSubmit, reset: resetPassword, watch, formState: { errors: passwordErrors } } = useForm();

  const newPassword = watch('newPassword');

  useEffect(() => {
    fetchCustomer();
  }, [uuid]);

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const [customerRes, statsRes, transactionsRes, walletRes] = await Promise.all([
        customerService.getCustomer(uuid),
        customerService.getCustomerStats(uuid),
        customerService.getCustomerTransactions(uuid, { limit: 20 }),
        customerAccountService.getCustomerAccounts(uuid)
      ]);

      if (customerRes.success) {
        setCustomer(customerRes.data);
        reset({
          fullName: customerRes.data.fullName,
          phone: customerRes.data.phone || '',
          email: customerRes.data.email || '',
          idType: customerRes.data.idType || '',
          idNumber: customerRes.data.idNumber || '',
          address: customerRes.data.address || '',
          notes: customerRes.data.notes || '',
          isVip: customerRes.data.isVip
        });
      }

      if (statsRes.success) {
        setStats(statsRes.data?.overview || null);
      }

      if (transactionsRes.success) {
        setTransactions(transactionsRes.data || []);
      }

      if (walletRes.success) {
        setWalletTransactions(walletRes.data?.transactions || []);
      }
    } catch (error) {
      toast.error(t('common.error'));
      navigate('/customers');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTransaction = async (txUuid) => {
    if (!window.confirm(t('transactions.confirmDelete') || 'Are you sure you want to delete this transaction?')) return;
    try {
      setSaving(true);
      const response = await transactionService.deleteTransaction(txUuid);
      if (response.success) {
        toast.success(t('transactions.transactionDeleted'));
        fetchCustomer();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWalletTransaction = async (txUuid) => {
    if (!window.confirm(t('common.confirmDelete') || 'Are you sure you want to delete this wallet transaction? This will revert the balance.')) return;
    try {
      setSaving(true);
      const response = await customerAccountService.deleteTransaction(txUuid);
      if (response.success) {
        toast.success(response.message || 'Wallet transaction deleted');
        fetchCustomer();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setSaving(true);
      const response = await customerService.updateCustomer(uuid, data);
      if (response.success) {
        toast.success(t('customers.customerUpdated'));
        setEditing(false);
        fetchCustomer();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      setSettingPassword(true);
      const response = await customerService.setPortalPassword(uuid, data.newPassword);
      if (response.success) {
        toast.success(t('customers.portalPasswordSet') || 'Portal password set successfully');
        setShowPasswordModal(false);
        resetPassword();
        fetchCustomer();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    } finally {
      setSettingPassword(false);
    }
  };

  const cancelEdit = () => {
    setEditing(false);
    reset({
      fullName: customer.fullName,
      phone: customer.phone || '',
      email: customer.email || '',
      idType: customer.idType || '',
      idNumber: customer.idNumber || '',
      address: customer.address || '',
      notes: customer.notes || '',
      isVip: customer.isVip
    });
  };

  const toggleBlock = async () => {
    try {
      if (customer.isBlocked) {
        await customerService.unblockCustomer(uuid);
        toast.success(t('customers.customerUnblocked'));
      } else {
        await customerService.blockCustomer(uuid, 'Blocked by admin');
        toast.success(t('customers.customerBlocked'));
      }
      fetchCustomer();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.error'));
    }
  };

  const idTypeOptions = [
    { value: '', label: t('common.select') },
    { value: 'passport', label: t('customers.passport') },
    { value: 'national_id', label: t('customers.nationalId') },
    { value: 'driver_license', label: t('customers.driverLicense') },
    { value: 'other', label: t('common.other') }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0';
    return new Intl.NumberFormat().format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" />
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">{t('common.noData')}</p>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/customers')}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className={`h-5 w-5 ${isRTL ? 'rotate-180' : ''}`} />
          </button>
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center">
              <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
                {customer.fullName?.charAt(0)?.toUpperCase() || 'C'}
              </span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customer.fullName}
                </h1>
                {customer.isVip && (
                  <StarSolidIcon className="h-5 w-5 text-yellow-500" />
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('customers.memberSince')}: {formatDate(customer.createdAt)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {!editing ? (
            <>
              <Button variant="secondary" onClick={() => { resetPassword(); setShowPasswordModal(true); }}>
                <KeyIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                {t('customers.setPortalPassword') || 'Set Portal Password'}
              </Button>
              <Button variant="secondary" onClick={toggleBlock}>
                {customer.isBlocked ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    {t('customers.unblock')}
                  </>
                ) : (
                  <>
                    <NoSymbolIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    {t('customers.block')}
                  </>
                )}
              </Button>
              <Button onClick={() => setEditing(true)}>
                <PencilIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                {t('common.edit')}
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
                <XMarkIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSubmit(onSubmit)} disabled={saving}>
                <CheckIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                {saving ? t('common.loading') : t('common.save')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Blocked Banner */}
      {customer.isBlocked && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
          <NoSymbolIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-800 dark:text-red-200">{t('customers.blocked')}</p>
            {customer.blockReason && (
              <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                {t('common.reason')}: {customer.blockReason}
              </p>
            )}
          </div>
        </div>
      )}

      <CustomerWallet customerUuid={uuid} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Customer Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Details */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <UserIcon className="h-5 w-5 text-primary-600" />
                {t('customers.customerDetails')}
              </h2>

              {editing ? (
                <form className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label={t('customers.fullName')}
                      {...register('fullName', { required: t('validation.required') })}
                      error={errors.fullName?.message}
                    />
                    <Input
                      label={t('customers.phone')}
                      {...register('phone')}
                    />
                    <Input
                      label={t('customers.email')}
                      type="email"
                      {...register('email')}
                    />
                    <Select
                      label={t('customers.idType')}
                      options={idTypeOptions}
                      {...register('idType')}
                    />
                    <Input
                      label={t('customers.idNumber')}
                      {...register('idNumber')}
                    />
                    <div className="flex items-center pt-6">
                      <input
                        type="checkbox"
                        id="isVip"
                        {...register('isVip')}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isVip" className="ml-2 rtl:ml-0 rtl:mr-2 flex items-center text-gray-700 dark:text-gray-300">
                        <StarIcon className="h-5 w-5 text-yellow-500 mr-1" />
                        {t('customers.vipCustomer')}
                      </label>
                    </div>
                  </div>
                  <Input
                    label={t('customers.address')}
                    {...register('address')}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('common.notes')}
                    </label>
                    <textarea
                      {...register('notes')}
                      rows={3}
                      className="input"
                    />
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('customers.phone')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{customer.phone || '-'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('customers.email')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{customer.email || '-'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IdentificationIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('customers.idType')}</p>
                      <p className="font-medium text-gray-900 dark:text-white capitalize">
                        {customer.idType?.replace('_', ' ') || '-'}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <IdentificationIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('customers.idNumber')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{customer.idNumber || '-'}</p>
                    </div>
                  </div>

                  <div className="md:col-span-2 flex items-start gap-3">
                    <DocumentTextIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('customers.address')}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{customer.address || '-'}</p>
                    </div>
                  </div>

                  {customer.notes && (
                    <div className="md:col-span-2 flex items-start gap-3">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('common.notes')}</p>
                        <p className="font-medium text-gray-900 dark:text-white">{customer.notes}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Recent Transactions */}
          <Card>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <ArrowPathIcon className="h-5 w-5 text-primary-600" />
                  {t('customers.recentTransactions') || 'Recent Transactions'}
                </h2>
                <Link
                  to={`/transactions?customer=${uuid}`}
                  className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  {t('common.viewAll') || 'View All'}
                </Link>
              </div>

              {transactions.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('customers.noTransactions') || 'No transactions found'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.date')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('transactions.type')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('transactions.fromAmount')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('transactions.toAmount')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.status')}</th>
                        {isAdmin() && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {transactions.map((tx) => (
                        <tr
                          key={tx.uuid}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                          onClick={() => navigate(`/transactions/${tx.uuid}`)}
                        >
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {formatDate(tx.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${tx.type === 'buy'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                              }`}>
                              {tx.type === 'buy' ? t('transactions.buy') : t('transactions.sell')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {formatCurrency(tx.amountIn || tx.fromAmount)} {tx.currencyIn?.code || tx.fromCurrency?.code || ''}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {formatCurrency(tx.amountOut || tx.toAmount)} {tx.currencyOut?.code || tx.toCurrency?.code || ''}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${tx.status === 'completed'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : tx.status === 'cancelled'
                                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                  : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                              }`}>
                              {tx.status}
                            </span>
                          </td>
                          {isAdmin() && (
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteTransaction(tx.uuid); }}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title={t('common.delete')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>

          {/* Wallet Transactions History */}
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BanknotesIcon className="h-5 w-5 text-primary-600" />
                {t('depositsLoans.recentTransactions') || 'Wallet History'}
              </h2>

              {walletTransactions.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('common.noData')}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.date')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.type')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.amount')}</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.notes')}</th>
                        {isAdmin() && <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('common.actions')}</th>}
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {walletTransactions.map((tx) => (
                        <tr key={tx.uuid} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                            {formatDate(tx.createdAt)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                tx.type === 'deposit' ? 'bg-green-100 text-green-800' :
                                tx.type.startsWith('withdrawal') ? 'bg-red-100 text-red-800' :
                                tx.type === 'loan_given' ? 'bg-orange-100 text-orange-800' :
                                'bg-blue-100 text-blue-800'
                              }`}>
                              {tx.type.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            <span className={tx.type === 'deposit' || tx.type === 'loan_repayment' ? 'text-green-600' : 'text-red-600'}>
                              {tx.amount} {tx.currencyCode}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 truncate max-w-xs">
                            {tx.notes}
                          </td>
                          {isAdmin() && (
                            <td className="px-4 py-3 text-sm">
                              <button
                                onClick={() => handleDeleteWalletTransaction(tx.uuid)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title={t('common.delete')}
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Statistics */}
          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <BanknotesIcon className="h-5 w-5 text-primary-600" />
                {t('customers.statistics')}
              </h3>

              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary-600">
                    {stats?.completedTransactions || customer.totalTransactions || 0}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('transactions.title')}</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">
                    ${formatCurrency(stats?.totalVolumeIn || customer.totalVolume || 0)}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t('reports.totalVolume')}</p>
                </div>

                {stats?.lastTransactionDate && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg text-center">
                    <p className="text-lg font-bold text-blue-600">
                      {formatDate(stats.lastTransactionDate)}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('customers.lastTransaction')}</p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Portal Access */}
          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <KeyIcon className="h-5 w-5 text-primary-600" />
                {t('customers.portalAccess') || 'Portal Access'}
              </h3>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('common.status')}</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${customer.hasPortalAccess
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                    {customer.hasPortalAccess
                      ? (t('customers.portalEnabled') || 'Enabled')
                      : (t('customers.portalDisabled') || 'Not Set Up')}
                  </span>
                </div>

                {customer.lastLoginAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('customers.lastLogin') || 'Last Login'}</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatDate(customer.lastLoginAt)}
                    </span>
                  </div>
                )}

                <Button
                  variant="secondary"
                  className="w-full mt-2"
                  onClick={() => { resetPassword(); setShowPasswordModal(true); }}
                >
                  <KeyIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                  {customer.hasPortalAccess
                    ? (t('customers.resetPortalPassword') || 'Reset Password')
                    : (t('customers.setPortalPassword') || 'Set Up Portal Access')}
                </Button>
              </div>
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <div className="p-6">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                {t('dashboard.quickActions') || 'Quick Actions'}
              </h3>

              <div className="space-y-2">
                <Link
                  to="/customers"
                  className="block w-full px-4 py-2 text-sm text-center text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('common.back')} {t('nav.customers')}
                </Link>

                <Link
                  to={`/transactions?customer=${uuid}`}
                  className="block w-full px-4 py-2 text-sm text-center text-primary-700 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded-lg transition-colors"
                >
                  {t('common.viewAll')} {t('transactions.title')}
                </Link>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Set Portal Password Modal */}
      <Modal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title={t('customers.setPortalPassword') || 'Set Portal Password'}
        size="sm"
      >
        <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('customers.setPortalPasswordDesc') || 'Set a password for this customer to access the customer portal.'}
          </p>

          {customer.email ? (
            <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
              <p className="text-xs text-gray-500 dark:text-gray-400 uppercase">{t('customers.email')}</p>
              <p className="font-medium text-gray-900 dark:text-white">{customer.email}</p>
            </div>
          ) : (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-3 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                {t('customers.noEmailWarning') || 'This customer has no email set. They will need an email to log in to the portal.'}
              </p>
            </div>
          )}

          <Input
            label={t('auth.newPassword')}
            type="password"
            {...registerPassword('newPassword', {
              required: t('validation.required'),
              minLength: { value: 6, message: t('validation.minLength', { min: 6 }) }
            })}
            error={passwordErrors.newPassword?.message}
          />

          <Input
            label={t('auth.confirmPassword')}
            type="password"
            {...registerPassword('confirmPassword', {
              required: t('validation.required'),
              validate: value => value === newPassword || t('validation.passwordMismatch')
            })}
            error={passwordErrors.confirmPassword?.message}
          />

          <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-4">
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)} disabled={settingPassword}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={settingPassword || !customer.email}>
              {settingPassword ? t('common.loading') : t('common.save')}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default CustomerDetailPage;
